import { apiClient, type SessionExpiries } from '../api/client';

export type AccountRole = 'admin' | 'planner';

export interface AuthUser {
  created_at: string;
  disabled_at: string | null;
  display_name: string;
  email: string;
  id: string;
  is_active: boolean;
  last_login_at: string | null;
  role: AccountRole;
  schema_version: string;
  updated_at: string;
  workspace_scope: 'single_organization';
}

export interface CurrentSessionResponse {
  absolute_expires_at: string;
  idle_expires_at: string;
  user: AuthUser;
}

export interface IdentityModeResponse {
  mode: 'local' | 'hosted';
}

export interface LoginResponse extends CurrentSessionResponse {
  csrf_token: string;
}

export interface PasswordResetRequestResponse {
  accepted: true;
  message: string;
}

export interface PasswordResetConfirmResponse {
  password_changed: true;
}

export interface UserPage {
  items: AuthUser[];
  limit: number;
  offset: number;
  total: number;
}

export interface AdminUserCreate {
  display_name: string;
  email: string;
  is_active: boolean;
  password: string;
  role: AccountRole;
}

export interface AdminUserUpdate {
  display_name?: string;
  email?: string;
  is_active?: boolean;
  role?: AccountRole;
}

export interface AuthenticationApi {
  clearClientSession?(): void;
  confirmPasswordReset(
    token: string,
    password: string,
  ): Promise<PasswordResetConfirmResponse>;
  createUser(payload: AdminUserCreate): Promise<AuthUser>;
  currentSession(signal?: AbortSignal): Promise<CurrentSessionResponse>;
  identityMode(signal?: AbortSignal): Promise<IdentityModeResponse>;
  listUsers(): Promise<UserPage>;
  login(email: string, password: string): Promise<LoginResponse>;
  logout(): Promise<void>;
  requestPasswordReset(email: string): Promise<PasswordResetRequestResponse>;
  setUserEnabled(accountId: string, enabled: boolean): Promise<AuthUser>;
  subscribeToUnauthorized?(handler: () => void): () => void;
  subscribeToSessionRefresh?(
    handler: (expiries: SessionExpiries) => void,
  ): () => void;
  updateUser(accountId: string, payload: AdminUserUpdate): Promise<AuthUser>;
}

export const browserAuthenticationApi: AuthenticationApi = {
  clearClientSession: () => apiClient.clearCsrfToken(),
  confirmPasswordReset: async (token, password) => {
    const response = await apiClient.post<PasswordResetConfirmResponse>(
      '/auth/password-reset/confirm',
      {
        json: { password, token },
        publicAuth: true,
      },
    );
    apiClient.clearCsrfToken();
    return response;
  },
  createUser: (payload) =>
    apiClient.post<AuthUser>('/auth/users', { json: payload }),
  currentSession: (signal) =>
    apiClient.get<CurrentSessionResponse>('/auth/me', {
      cache: 'no-store',
      ...(signal === undefined ? {} : { signal }),
    }),
  identityMode: (signal) =>
    apiClient.get<IdentityModeResponse>('/identity-mode', {
      cache: 'no-store',
      ...(signal === undefined ? {} : { signal }),
    }),
  listUsers: () =>
    apiClient.get<UserPage>(
      '/auth/users?offset=0&limit=200&include_disabled=true',
      { cache: 'no-store' },
    ),
  login: (email, password) =>
    apiClient.post<LoginResponse>('/auth/login', {
      json: { email, password },
      publicAuth: true,
    }),
  logout: async () => {
    await apiClient.post<void>('/auth/logout');
    apiClient.clearCsrfToken();
  },
  requestPasswordReset: (email) =>
    apiClient.post<PasswordResetRequestResponse>(
      '/auth/password-reset/request',
      {
        json: { email },
        publicAuth: true,
      },
    ),
  setUserEnabled: (accountId, enabled) =>
    apiClient.post<AuthUser>(
      '/auth/users/' + encodeURIComponent(accountId) + (enabled ? '/enable' : '/disable'),
    ),
  subscribeToUnauthorized: (handler) => apiClient.onUnauthorized(handler),
  subscribeToSessionRefresh: (handler) => apiClient.onSessionRefresh(handler),
  updateUser: (accountId, payload) =>
    apiClient.patch<AuthUser>(
      '/auth/users/' + encodeURIComponent(accountId),
      { json: payload },
    ),
};
